import {collection, getDocsFromServer, query, where} from "firebase/firestore";
import {Alert, Button, Col, Form, FormSelect, Modal, Row, Spinner} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft, faSave, faRotateRight} from "@fortawesome/free-solid-svg-icons";
import {FormEvent, useEffect, useState} from "react";
import {
    dateTimeToString,
    dateToString,
    removeEmptyFields,
    showErrorsListInToast, textToFloat,
    zeroToEmptyString,
} from "../../tools";
import {RREmployeeType} from "../../types/RREmployeeType";
import {appFirestore} from "../../../index";
import ReactDatePicker from "react-datepicker";
import {MaybeDate} from "../../types/Maybe";
import {RRDEPARTMENT_MONTAGE, RRDEPARTMENT_MONTAGE_LABEL} from "../../types/RRDepartmentType";
import {EMPTY_RR_EVENT, RREventType} from "../../types/RREventType";
import {RREventStatusType, RREventStatusTypeList} from "../../types/RREventStatusType";
import {useAppSelector} from "../../hooks";
import {selectDepartment} from "../../../features/AppStateSlice";
import {calendarDataConverter} from "../../../firebase/FirebaseCalendarLoader";
import {RRCalendarDaysType} from "../../types/RRCalendarDayType";
import {saveScheduledEvent} from "../../calendar/scheduleMutations";
import {eventDateLabel, eventDayKey, eventTimeLabel, isWithinEventPeriod} from "../../calendar/eventPeriod";


export interface WarehouseObjectDetailsProps {
    zIndex?: number;
    event: RREventType | null;
    onDone: () => void;
}

export function EventsDetails({
                                  zIndex,
                                  event,
                                  onDone
                              }: WarehouseObjectDetailsProps) {
    const currentEmployee = useAppSelector(state => state.app.currentEmployee);
    const department = useAppSelector(selectDepartment);
    const departmentEmptyEvent = {...EMPTY_RR_EVENT, department: department};
    const [saving, setSaving] = useState(false);
    const [editEvent, setEditEvent] = useState<RREventType>(departmentEmptyEvent);
    const [startDate, setStartDate] = useState<Date>(event?.firstDate || new Date());
    const [leaveDate, setLeaveDate] = useState<MaybeDate>(event?.lastDate);
    const [periodConflicts, setPeriodConflicts] = useState<RRCalendarDaysType>([]);
    const [checkingPeriod, setCheckingPeriod] = useState(false);
    const [periodError, setPeriodError] = useState("");
    const [reloadPeriod, setReloadPeriod] = useState(0);

    useEffect(() => {
        if (event) {
            setStartDate(event.firstDate ?? new Date());
            setLeaveDate(event.lastDate);
            setEditEvent({...event});
        }
    }, [event]);

    useEffect(
        () => {
            if (startDate && leaveDate && startDate > leaveDate) setLeaveDate(startDate);
        }, [startDate, leaveDate]
    );

    const save = async (data: RREventType) => {
        if (saving || checkingPeriod || periodConflicts.length || periodError) return;
        if (!canSaveCompletedEvent()) {
            showErrorsListInToast("Внимание", ["Събитието е завършено и не може да бъде редактирано"]);
            return;
        }
        setSaving(true);
        try {
            await saveScheduledEvent({...data, lastDate: data.lastDate || null}, !event?.id);
            onDone();
        } catch (error) {
            const message = error instanceof Error ? error.message : "Събитието не може да бъде запазено.";
            setPeriodError(message);
            showErrorsListInToast("Събитието не е запазено", [message]);
        } finally {
            setSaving(false);
        }
    }

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data: RREventType = {
            ...editEvent,
            eventCategory: "MONTAGE", //event.target.elements.eventCategory.value,
            isDeleted: false,
            isCatering: department === "catering",
            isMontage: department === "montage",
            isStore: department === "store",
            firstDate: startDate,
            lastDate: leaveDate,
        };

        const errors: string[] = [];
        if (leaveDate && eventDayKey(leaveDate) < eventDayKey(startDate)) errors.push("Крайната дата не може да е преди началната.");
        if (!data.eventName) {
            errors.push("Не сте попълнили полето за наименование на събитието");
        }

        if (!data.eventShortName) {
            errors.push("Не сте попълнили полето за кратко наименование на събитието");
        }

        if (!data.eventNumber) {
            errors.push("Не сте попълнили полето за номер на събитието");
        }

        if (errors.length === 0) {
            save(data);
        } else {
            showErrorsListInToast("Внимание", errors);
        }
    }

    function canSaveCompletedEvent() {
        return event?.eventStatus !== "ЗАВЪРШЕНО" || ["DRulMuqC9vM7tud45ngd", "OLthfQiAHym06Gif3oNk"].includes(currentEmployee?.id ?? "");
    }

    useEffect(
        () => {
            let cancelled = false;
            setPeriodError("");
            const changed = event?.firstDate && (eventDayKey(startDate) !== eventDayKey(event.firstDate) ||
                eventDayKey(leaveDate || startDate) !== eventDayKey(event.lastDate || event.firstDate));
            if (!event?.id || !changed) {
                setPeriodConflicts([]);
                setCheckingPeriod(false);
                return;
            }
            setCheckingPeriod(true);
            getDocsFromServer(query(collection(appFirestore, "calendar"), where("eventId", "==", event.id)))
                .then(snapshot => {
                    if (!cancelled) setPeriodConflicts(calendarDataConverter(snapshot.docs)
                        .filter(item => !isWithinEventPeriod(item.eventDayStart, startDate, leaveDate)));
                })
                .catch(error => {
                    if (!cancelled) setPeriodError(error instanceof Error ? error.message : "Проверката на периода е неуспешна. Опитайте отново.");
                })
                .finally(() => { if (!cancelled) setCheckingPeriod(false); });
            return () => { cancelled = true; };
        }, [event, startDate, leaveDate, reloadPeriod]
    );

    return (
        <><Form className={"p-2"} onSubmit={handleSubmit}>
            <Modal.Body>
                <h5 className={"mb-3"}>
                    {event ? "Корекция" : "Добавяне"} на събитие
                    {event && <Button variant={"outline-secondary"} size={"sm"} className={"ms-3"}
                        title={"Провери отново планираните записи"} aria-label={"Провери отново планираните записи"}
                        disabled={checkingPeriod || saving} onClick={() => setReloadPeriod(value => value + 1)}>
                        <FontAwesomeIcon icon={faRotateRight}/>
                    </Button>}
                </h5>

                <Row>
                    <Col>
                        <Row>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Номер:</Form.Label>
                                    <Form.Control type="text"
                                                  value={editEvent?.eventNumber || ""}
                                                  onChange={e => setEditEvent({
                                                      ...editEvent,
                                                      eventNumber: e.target.value || ""
                                                  })}
                                                  required/>
                                </Form.Group>
                            </Col>
                            <Col xs={8}>
                                <Form.Group>
                                    <Form.Label>Наименование на събитието:</Form.Label>
                                    <Form.Control type="text"
                                                  value={editEvent?.eventName || ""}
                                                  onChange={e => setEditEvent({
                                                      ...editEvent,
                                                      eventName: e.target.value || ""
                                                  })}
                                                  required/>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={4}>
                                <Form.Group>
                                    <Form.Label>Кратко наименование на събитието:</Form.Label>
                                    <Form.Control type="text"
                                                  value={editEvent?.eventShortName || ""}
                                                  onChange={e => setEditEvent({
                                                      ...editEvent,
                                                      eventShortName: e.target.value || ""
                                                  })}
                                                  required/>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Място за провеждане:</Form.Label>
                                    <Form.Control type="text"
                                                  value={editEvent?.place || ""}
                                                  onChange={e => setEditEvent({
                                                      ...editEvent,
                                                      place: e.target.value || ""
                                                  })}
                                                  required/>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Начална дата / час:</Form.Label>
                                    <ReactDatePicker onChange={date => setStartDate(date || new Date())}
                                                     required
                                                     dateFormat="dd.MM.yyyy HH:mm"
                                                     selected={startDate}
                                                     className={"form-control"}
                                                     showTimeSelect
                                                     timeCaption={"Час"}
                                                     timeIntervals={30}
                                                     value={dateTimeToString(startDate)}
                                                     maxDate={leaveDate}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Крайна дата / час:</Form.Label>
                                    <ReactDatePicker onChange={date => setLeaveDate(date)}
                                                     isClearable
                                                     dateFormat="dd.MM.yyyy HH:mm"
                                                     selected={leaveDate}
                                                     className={"form-control"}
                                                     showTimeSelect
                                                     timeCaption={"Час"}
                                                     timeIntervals={30}
                                                     value={dateTimeToString(leaveDate)}
                                                     minDate={startDate}                                                     shouldCloseOnSelect={true}
                                    />
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group>
                                    <Form.Label>Статус:</Form.Label>
                                    <FormSelect
                                        disabled={!canSaveCompletedEvent()}
                                        value={editEvent?.eventStatus || ""}
                                        onChange={e => setEditEvent({
                                            ...editEvent,
                                            eventStatus: e.target.value as RREventStatusType
                                        })}
                                        required>
                                        {
                                            RREventStatusTypeList.map(
                                                value =>
                                                    <option value={value} key={value}>{value}</option>
                                            )
                                        }
                                    </FormSelect>
                                </Form.Group>
                            </Col>
                            <Col xs={2}>
                            {/*    Select color for the event. Options are: info, warning, primary, secondary */}
                                <Form.Group>
                                    <Form.Label>Цвят:</Form.Label>
                                    <FormSelect
                                        value={editEvent?.eventColor}
                                        onChange={e => setEditEvent({
                                            ...editEvent,
                                            eventColor: e.target.value
                                        })}
                                    >
                                        <option value={""} className={""}>Без цвят</option>
                                        <option value={"bg-info"} >Тюркоазен</option>
                                        <option value={"bg-warehouse"}>Жълт</option>
                                        <option value={"bg-warning"}>Оранжев</option>
                                        <option value={"bg-primary"}>Син</option>
                                        <option value={"bg-secondary-light"}>Сив</option>
                                    </FormSelect>
                                </Form.Group>
                            </Col>
                        </Row>

                        {
                            (periodConflicts.length > 0 || checkingPeriod || periodError) &&
                            <Row className={"mt-3"}>
                                <Col>
                                <Alert variant={periodConflicts.length || periodError ? "danger" : "info"} aria-busy={checkingPeriod}>
                                    {checkingPeriod && <div><Spinner size={"sm"} className={"me-2"}/>Проверка на планираните записи…</div>}
                                    {periodError && <div style={{whiteSpace: "pre-line"}}>{periodError}</div>}
                                    {periodConflicts.length > 0 && <>
                                        <p>Периодът не може да се запази. Следните записи остават извън него. Коригирайте периода или планирането на служителите.</p>
                                        <ul className={"mb-0"}>{periodConflicts.map(item => <li key={item.id}>
                                            <strong>{item.employeeName}</strong>: {eventDateLabel(item.eventDayStart)} от {eventTimeLabel(item.eventDayStart)}
                                            {item.eventDayEnd ? ` до ${eventDateLabel(item.eventDayEnd)} ${eventTimeLabel(item.eventDayEnd)}` : " (без краен час)"}
                                        </li>)}</ul>
                                    </>}
                                </Alert>
                                </Col>
                            </Row>
                        }

                        <Row className={"mt-3"}>
                            <Col>
                                <h6>Данни за клиента:</h6>
                                <Row className={"bg-secondary-light"}>
                                    <Col>
                                        <Row>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label>Наименование:</Form.Label>
                                                    <Form.Control type="text"
                                                                  onChange={e => setEditEvent({
                                                                      ...editEvent,
                                                                      clientName: e.target.value || ""
                                                                  })
                                                                  }
                                                                  value={editEvent?.clientName || ""}
                                                                  required={department === "montage"}/>
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label>Телефон:</Form.Label>
                                                    <Form.Control type="text"
                                                                  onChange={e => setEditEvent({
                                                                      ...editEvent,
                                                                      clientPhone: e.target.value || ""
                                                                  })
                                                                  }
                                                                  value={editEvent?.clientPhone || ""}
                                                                  required={department === "montage"}/>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label>Човек за контакт:</Form.Label>
                                                    <Form.Control type="text"
                                                                  value={editEvent?.clientSpoc || ""}
                                                                  onChange={e => setEditEvent({
                                                                      ...editEvent,
                                                                      clientSpoc: e.target.value || ""
                                                                  })}
                                                                  required={department === "montage"}/>
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group>
                                                    <Form.Label>E-mail:</Form.Label>
                                                    <Form.Control type="text"
                                                                  value={editEvent?.clientEmail || ""}
                                                                  onChange={e => setEditEvent({
                                                                      ...editEvent,
                                                                      clientEmail: e.target.value || ""
                                                                  })}
                                                                  required={department === "montage"}/>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row><Col>&nbsp;</Col></Row>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Col>

                </Row>

            </Modal.Body>
            <Modal.Footer>
                <Row className={"w-100"}>
                    {
                        saving && <Col xs={12} className={"text-center"}>
                            <Spinner animation={"border"}/>
                        </Col>
                    }

                    {
                        !saving &&
                        <>
                            <Col xs={6}>
                                <Button variant={"outline-secondary"} onClick={() => {
                                    onDone()
                                }}>
                                    <FontAwesomeIcon icon={faArrowLeft}/> Към списъка
                                </Button>
                            </Col>
                            <Col xs={6} className={"text-end"}>
                                <Button type={"submit"} disabled={!canSaveCompletedEvent() || checkingPeriod || periodConflicts.length > 0 || !!periodError}>
                                    <FontAwesomeIcon icon={faSave}/> Запиши
                                </Button>
                            </Col>
                        </>
                    }
                </Row>
            </Modal.Footer>
        </Form>
        </>
    )
}
